import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { BASE_URL } from '../apiConfig';

// Branch AR / Credit AR daily reports. reportType is "branch" or "credit"; each keeps its own state.

const createQueryParams = (page, pageSize, filters) => {
  const params = new URLSearchParams();

  if (page) params.append('page', page);
  if (pageSize) params.append('page_size', pageSize);
  if (filters) {
    Object.keys(filters).forEach((key) => {
      if (filters[key]) params.append(key, filters[key]);
    });
  }

  return params.toString();
};

export const fetchARReport = createAsyncThunk(
  'arReport/fetchARReport',
  async ({ jwtBearer, reportType, page, pageSize, filters } = {}, { rejectWithValue }) => {
    try {
      const queryParams = createQueryParams(page, pageSize, filters);
      const response = await axios.get(
        `${BASE_URL}ar/${reportType}/data/${queryParams ? `?${queryParams}` : ''}`,
        {
          headers: {
            Authorization: `Bearer ${jwtBearer}`
          }
        }
      );
      return response.data;
    } catch (error) {
      if (error.response && error.response.status === 401) {
        window.location.reload();
      }
      console.error('Fetch error:', error);
      return rejectWithValue(error.response ? error.response.data : error.message);
    }
  }
);

export const downloadARReport = createAsyncThunk(
  'arReport/downloadARReport',
  async ({ jwtBearer, reportType }, { rejectWithValue }) => {
    try {
      const fileName = reportType === 'credit' ? 'CREDIT_AR_REPORT.csv' : 'BRANCH_AR_REPORT.csv';

      const response = await fetch(`${BASE_URL}ar/report/download/`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${jwtBearer}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ type: reportType }),
      });

      if (!response.ok) {
        throw new Error('Failed to generate report');
      }

      const blob = await response.blob();
      const downloadUrl = URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error('Error downloading AR report:', error);
      return rejectWithValue(error.message);
    }
  }
);

const emptyReport = {
  data: [],
  loading: false,
  error: null,
  totalCount: 0,
};

const arReportSlice = createSlice({
  name: 'arReport',
  initialState: {
    branch: { ...emptyReport },
    credit: { ...emptyReport },
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchARReport.pending, (state, action) => {
        const report = state[action.meta.arg.reportType];
        report.loading = true;
        report.error = null;
      })
      .addCase(fetchARReport.fulfilled, (state, action) => {
        const report = state[action.meta.arg.reportType];
        report.loading = false;
        report.data = action.payload.data.ar_data;
        report.totalCount = action.payload.data.total_ar_data;
      })
      .addCase(fetchARReport.rejected, (state, action) => {
        const report = state[action.meta.arg.reportType];
        report.loading = false;
        report.error = action.payload?.message || action.error.message;
      });
  }
});

export default arReportSlice.reducer;
